<template>
  <div class="flex h-screen bg-gray-100">
    <!-- Sidebar de navigation admin -->
    <div 
      :class="[
        'bg-blue-950 flex flex-col py-6 transition-all duration-300',
        collapsed ? 'w-16 px-2' : 'w-64 px-4'
      ]"
    >
      <!-- Logo/Brand -->
      <NuxtLink 
        :to="localePath('/')"
        :class="[
          'flex items-center mb-8 transition-all duration-300',
          collapsed ? 'justify-center px-0' : 'gap-3 px-3'
        ]"
      >
        <img 
          src="/logo_simple_orders.png" 
          alt="Logo DJUST" 
          class="w-8 h-8 object-contain flex-shrink-0"
        />
        <span 
          v-if="!collapsed" 
          class="text-white font-semibold text-lg whitespace-nowrap"
        >
          DJUST Admin
        </span>
      </NuxtLink>

      <!-- Navigation Menu -->
      <UNavigationMenu 
        :items="navigationItems" 
        orientation="vertical"
        :collapsed="collapsed"
        :tooltip="true"
        :ui="{
          root: 'w-full',
          list: 'flex-col',
          link: [
            'rounded-lg text-blue-100 hover:bg-blue-900 hover:text-white data-[state=open]:bg-blue-800 mb-4',
            collapsed ? 'px-2 py-2.5 justify-center' : 'px-3 py-2.5'
          ],
          linkLeadingIcon: 'text-blue-300 group-hover:text-white',
          linkLabel: collapsed ? 'hidden' : '',
          label: 'text-xs uppercase tracking-wider text-blue-400 font-semibold px-3 py-2'
        }"
        highlight
        highlight-color="primary"
        class="flex-1"
      />

      <!-- Bottom section -->
      <div class="mt-auto pt-6  border-t-0 border-blue-800 space-y-2">
        <!-- Logout -->
        <div class="bg-blue-900 rounded-t-lg text-blue-300 px-1 w-full text-center pt-2 pb-1 text-xs mb-0">Admin</div>
        <UNavigationMenu 
          :items="bottomItems" 
          orientation="vertical"
          :tooltip="false"
          :collapsed="true"
          :popover="true"
          :ui="{
            root: 'w-full',
            list: 'flex-col gap-1 bg-blue-900 rounded-b-lg',
            link: [
              'rounded-lg text-blue-100 hover:bg-blue-900 hover:text-white data-[state=open]:bg-blue-800 mb-4',
              collapsed ? 'px-2 py-2.5 justify-center' : 'px-3 py-2.5'
            ],
            linkLeadingIcon: 'text-blue-300 group-hover:text-white',
            linkLabel: collapsed ? 'hidden' : '',
            separator: 'border-t border-blue-800',
            label: 'text-xs uppercase tracking-wider text-blue-400 font-semibold px-3 py-2'
          }"
          highlight
          highlight-color="primary"
          class="flex-1"
        />
        <UNavigationMenu 
          :items="logoutItems" 
          orientation="vertical"
          :tooltip="false"
          :collapsed="true"
          :popover="true"
          :ui="{
            root: 'w-full',
            list: 'flex-col gap-1',
            link: [
              'rounded-lg text-blue-100 hover:bg-blue-900 hover:text-white data-[state=open]:bg-blue-800 mb-4',
              collapsed ? 'px-2 py-2.5 justify-center' : 'px-3 py-2.5'
            ],
            linkLeadingIcon: 'text-blue-300 group-hover:text-white',
            linkLabel: collapsed ? 'hidden' : '',
            separator: 'border-t border-blue-800',
            label: 'text-xs uppercase tracking-wider text-blue-400 font-semibold px-3 py-2'
          }"
          highlight
          highlight-color="primary"
          class="flex-1"
        />
        <!-- Language Switcher -->
        <div :class="collapsed ? 'px-0 flex justify-center' : 'px-3'">
          <LanguageSwitcher compact />
        </div>

        <!-- Toggle collapse button -->
        <button
          v-if="!collapsed"
          @click="collapsed = !collapsed"
          :class="[
            'w-full flex items-center rounded-lg text-blue-300 hover:bg-blue-900 hover:text-white transition-colors',
            collapsed ? 'px-2 py-2.5 justify-center' : 'px-3 py-2.5 gap-3'
          ]"
          :title="collapsed ? t('AdminLayout.navigation.expand') : t('AdminLayout.navigation.collapse')"
        >
          <UIcon 
            :name="collapsed ? 'i-lucide-panel-left-open' : 'i-lucide-panel-left-close'" 
            class="w-5 h-5 flex-shrink-0"
          />
          <span v-if="!collapsed" class="text-sm">
            {{ t('AdminLayout.navigation.collapse') }}
          </span>
        </button>
      </div>
    </div>

    <!-- Contenu principal -->
    <div class="flex-1 flex flex-col overflow-hidden">
      <slot />
    </div>

    <!-- Modale de session expirée -->
    <SessionExpiredModal
      :show="showSessionExpiredModal"
      :message="sessionErrorMessage"
      @reconnect="handleReconnect"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import type { NavigationMenuItem } from '@nuxt/ui'

defineOptions({
  name: 'AdminLayout',
})

const route = useRoute()
const { logout } = useDjustAuthAdmin()
const localePath = useLocalePath()
const { t } = useI18n()

// Gestion de la session expirée
const { showSessionExpiredModal, sessionErrorMessage, handleReconnect } = useAuthHandler()

// État collapsed de la sidebar
const collapsed = ref(true)

// Route actuelle pour highlighter le bon item de navigation
const currentRoute = computed(() => {
  const pathWithoutLocale = route.path.replace(/^\/[a-z]{2}-[A-Z]{2}/, '')
  return pathWithoutLocale || route.path
})

// Items de navigation principale
const navigationItems = computed<NavigationMenuItem[]>(() => [[
  {
    label: t('AdminLayout.navigation.emails'),
    icon: 'i-lucide-file-text',
    to: localePath('/admin/emails'),
    active: currentRoute.value.startsWith('/admin/emails')
  },
     {
     label: t('AdminLayout.navigation.orders'),
     icon: 'i-lucide-shopping-cart',
     to: localePath('/admin/orders'),
     active: currentRoute.value === '/admin/orders'
   },
   {
     label: t('AdminLayout.navigation.customers'),
     icon: 'i-lucide-users',
     to: localePath('/admin/customers'),
    active: currentRoute.value === '/admin/customers'
   },
   {
     label: t('AdminLayout.navigation.training'),
     icon: 'i-lucide-brain',
     to: localePath('/admin/training'),
     active: currentRoute.value === '/admin/training'
   },

  // Items commentés pour référence future:
  // {
  //   label: t('AdminLayout.navigation.dashboard'),
  //   icon: 'i-lucide-layout-dashboard',
  //   to: localePath('/admin/dashboard'),
  //   active: currentRoute.value === '/admin/dashboard'
  // },

  // {
  //   label: t('AdminLayout.navigation.settings'),
  //   icon: 'i-lucide-settings',
  //   to: localePath('/admin/settings'),
  //   active: currentRoute.value === '/admin/settings'
  // }
]
])

// Items du bas (logout)
const bottomItems = computed<NavigationMenuItem[]>(() => [
  [  
    {
     label: t('AdminLayout.navigation.sales'),
     icon: 'i-lucide-user-cog',
     to: localePath('/admin/sales'),
     active: currentRoute.value === '/admin/sales'
    },
    {
        label: t('AdminLayout.navigation.settings'),
        icon: 'i-lucide-settings',
        to: localePath('/admin/settings'),
        active: currentRoute.value.startsWith('/admin/settings')
      }


]])

const logoutItems = computed<NavigationMenuItem[]>(() => [
  [
  {
    label: t('AdminLayout.navigation.logout'),
    icon: 'i-lucide-log-out',
    onSelect: handleLogout
  }
]])


// Gestion de la déconnexion
const handleLogout = async () => {
  try {
    await logout()
    await navigateTo(localePath('/auth/login'))
  } catch (error) {
    console.error(t('AdminLayout.errors.logoutError'), error)
  }
}
</script>

<style scoped>
/* Override NavigationMenu styles pour le thème sombre */
:deep([data-slot="navigation-menu"]) {
  --ui-bg: transparent;
}

:deep([data-slot="navigation-menu-link"][data-active="true"]) {
  background-color: rgb(37 99 235); /* bg-blue-600 */
  color: white;
}

:deep([data-slot="navigation-menu-link"][data-active="true"] [data-slot="icon"]) {
  color: white;
}

/* Tooltip pour les items en mode collapsed */
:deep([data-slot="navigation-menu-link"]) {
  position: relative;
}
</style>
